import { useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { BACKEND_URL, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BulkDeleteDialog from "@/components/BulkDeleteDialog";

const FOLDERS = ["Plans & Specifications", "Contracts & Variations", "H&S Documents",
                 "Subcontractor Agreements", "Council/Consents", "Correspondence",
                 "Invoices & Quotes", "Photos"];

export default function DocumentsTab() {
  const { project } = useOutletContext();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [folder, setFolder] = useState("Correspondence");
  const [name, setName] = useState("");
  const [link, setLink] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);
  const isAdmin = user?.role === "Admin";

  async function load() {
    const { data } = await api.get(`/projects/${project.id}/documents`);
    setDocs(data);
    setSelected(prev => new Set([...prev].filter(id => data.some(d => d.id === id))));
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [project.id]);

  async function upload(e) {
    e.preventDefault();
    const f = fileRef.current.files?.[0]; if (!f) return;
    const fd = new FormData();
    fd.append("file", f); fd.append("folder", folder); fd.append("name", name || f.name);
    await api.post(`/projects/${project.id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    setName(""); fileRef.current.value = ""; load();
  }
  async function share(d) {
    const { data } = await api.post(`/documents/${d.id}/share-link`, { expires_days: 7 });
    const url = `${window.location.origin}/d/${data.token}`;
    setLink({ url, expires_at: data.expires_at });
  }

  function toggleAll() {
    if (selected.size === docs.length) setSelected(new Set());
    else setSelected(new Set(docs.map(d => d.id)));
  }
  function toggleOne(id) {
    const n = new Set(selected);
    n.has(id) ? n.delete(id) : n.add(id);
    setSelected(n);
  }
  const selectedList = docs.filter(d => selected.has(d.id));

  async function bulkConfirm({ confirm_text }) {
    const r = await api.post("/documents/bulk-delete", { ids: Array.from(selected), confirm_text });
    setMsg(`Deleted ${r.data.soft_deleted} documents. Restorable from Projects → Deleted items for 30 days.`);
    setSelected(new Set());
    await load();
  }

  return (
    <div className="space-y-3" data-testid="docs-tab">
      <form onSubmit={upload} className="card grid grid-cols-1 md:grid-cols-5 gap-2">
        <select className="input" value={folder} onChange={e=>setFolder(e.target.value)}>
          {FOLDERS.map(f => <option key={f}>{f}</option>)}
        </select>
        <input className="input" placeholder="Name (optional)" value={name} onChange={e=>setName(e.target.value)}/>
        <input ref={fileRef} type="file" className="text-white text-sm md:col-span-2"/>
        <button className="btn-silver" data-testid="btn-upload-doc">Upload</button>
      </form>
      {link && <div className="card text-sm">Share link (7-day): <a className="hub-link" href={link.url}>{link.url}</a> — expires {fmtDate(link.expires_at)}</div>}
      {msg && <div className="text-green-400 text-sm" data-testid="doc-list-msg">{msg}</div>}
      <div className="card">
        {isAdmin && selected.size > 0 && (
          <div className="mb-2">
            <button className="btn-ghost text-red-400 text-sm" data-testid="btn-bulk-delete-docs"
                    onClick={() => setBulkOpen(true)}>
              Delete selected ({selected.size})
            </button>
          </div>
        )}
        <table className="table"><thead><tr>
          {isAdmin && (
            <th className="w-8"><input type="checkbox" data-testid="doc-select-all"
              checked={docs.length > 0 && selected.size === docs.length}
              onChange={toggleAll} aria-label="Select all"/></th>
          )}
          <th>Folder</th><th>Name</th><th>Version</th><th>Size</th><th></th></tr></thead>
          <tbody>{docs.map(d=>(
            <tr key={d.id} data-testid={`doc-${d.id}`}>
              {isAdmin && (
                <td><input type="checkbox" data-testid={`doc-select-${d.id}`}
                  checked={selected.has(d.id)} onChange={() => toggleOne(d.id)}/></td>
              )}
              <td>{d.folder}</td><td>{d.name || d.filename}</td><td>v{d.version||1}</td>
              <td>{d.size ? (d.size/1024).toFixed(1)+" KB" : "—"}</td>
              <td><button className="text-xs hub-link" onClick={()=>share(d)}>Share link</button></td>
            </tr>
          ))}
          {docs.length===0 && <tr><td colSpan={isAdmin ? "6" : "5"} className="opacity-50">No documents yet.</td></tr>}</tbody></table>
      </div>
      <BulkDeleteDialog open={bulkOpen} onClose={() => setBulkOpen(false)}
        count={selected.size} items={selectedList.map(d => `${d.folder} · ${d.name || d.filename}`)}
        hasApproved={false}
        onConfirm={bulkConfirm}
        labels={{ entity: "documents", noun: "document" }}/>
    </div>
  );
}
